import {head, type NonEmptyArray} from 'web-app/util/non-empty-array';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {AUTOMATIC_PRICING_GROUP_ID} from '../constants';
import type * as Types from '../types';

export const transformTuitionsOrFeesToProratedItem = (
    customPeriodType: 'start' | 'end',
    customPeriodDates: null | Types.CustomPeriodDates,
    tuitionsOrFees: NonEmptyArray<Types.Tuition | Types.Fee>,
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'],
): Array<Types.ProratedItem> => {
    return tuitionsOrFees
        .map<null | Types.ProratedItem>(item => {
            const priceFromPricingGroup =
                selectedPricingGroup === AUTOMATIC_PRICING_GROUP_ID
                    ? head(item.pricingGroups).price
                    : item.pricingGroups.find(pricingGroup => pricingGroup.id === selectedPricingGroup)?.price;
            const customPeriod = customPeriodType === 'start' ? item.customStartPeriod : item.customEndPeriod;

            if (hasValue(customPeriod) && hasValue(customPeriod.price)) {
                return {
                    id: item.id,
                    title: item.title,
                    price: customPeriod?.price,
                };
            }

            if (!hasValue(priceFromPricingGroup)) {
                return null;
            }

            if (!hasValue(customPeriodDates)) {
                return {
                    id: item.id,
                    title: item.title,
                    price: priceFromPricingGroup,
                };
            }

            const proratedPrice = calculateProratedPrice(
                head(tuitionsOrFees).type,
                priceFromPricingGroup,
                customPeriodDates,
            );
            if (hasValue(proratedPrice)) {
                return {
                    id: item.id,
                    title: item.title,
                    price: proratedPrice,
                };
            }
            return null;
        })
        .filter(hasValue);
};

const calculateProratedPrice = (
    billingType: Types.BillingType,
    price: number,
    {startDate, endDate}: Types.CustomPeriodDates,
): null | number => {
    switch (billingType) {
        case 'monthly': {
            const daysInMonth = startDate.clone().daysInMonth();
            const customPeriodInDays = Math.abs(startDate.clone().diff(endDate, 'day', true));
            const proratedPrice = (price / daysInMonth) * customPeriodInDays;
            return parseFloat(proratedPrice.toFixed(2));
        }
        case 'weekly': {
            const daysInWeek = 7;
            const customPeriodInDays = Math.abs(startDate.clone().diff(endDate, 'day', true));
            const proratedPrice = (price / daysInWeek) * customPeriodInDays;
            return parseFloat(proratedPrice.toFixed(2));
        }
        default:
            return null;
    }
};
